import { ChatModels } from "../constants.js";
import sha256 from "crypto-js/sha256.js";
import {is_on_premises, parseJSON, publicChatData} from "../App/utils.js";
import {MESSAGE} from "../App/constants.js";
import {kbUserDataLoaded, pageRandomNumber, toYAML} from "../utils";
import {createEmbedding} from "../API/OpenAIAPI";
import {db} from "../Engines/Dexie";
import {spaLogger} from "../API/APP/SPALoggerAPI";
import {decryptItemByAttributes} from "../Item/utils";
import {colorEasyRed} from "../App/theme";

export const generateChatId = () => `${+new Date()}-${Math.floor(100000 + Math.random() * 900000)}`
export const generateMsgId = () => `${+new Date()}-${Math.floor(100000 + Math.random() * 900000)}`
export const generateId = () => `${+new Date()}`

export const getContentRenderURL = (kbid) => {
    if (!kbid) return null;

    return is_on_premises
        ? `http://127.0.0.1:38592/contentRender.bundle.js`
        : 'https://openkbs-files.s3-accelerate.amazonaws.com/frontend/' + encodeURIComponent(kbid) + '/Frontend/dist/contentRender.bundle.js?v=' + pageRandomNumber
}

export const buildInputQuery = (lastMessageContent, messages) => {
    return lastMessageContent + '\n\n' + messages
        .map(msg => typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content))
        .join('\n\n')
}

export const searchIndexedDB = async ({query, KB, maxTokens, renderSettings}) => {
    if (!query) return
    try {
        const res = await createEmbedding(query, KB?.embeddingModel, KB);
        const inputVector = res?.data[0]?.embedding.slice(0, (KB?.embeddingDimension - 1))
        const knnResponse = await db.knn({inputVector, maxTokens, renderSettings})
        return knnResponse
    } catch (e) {
        spaLogger('Failed to create embeddings')
        console.error(e)
    }

};

export function approximateTokenSize(input) {
    const WHITESPACE_RE = /^\s+$/
    const CJK_RE = /[\u4E00-\u9FFF\u3400-\u4DBF\u3000-\u303F\uFF00-\uFFEF\u30A0-\u30FF\u2E80-\u2EFF\u31C0-\u31EF\u3200-\u32FF\u3300-\u33FF\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uD7B0-\uD7FF]/
    const NUMERIC_SEQUENCE_RE = /[\d.,]+/
    const PUNCTUATION_RE = /[.,!?;'"„“”‘’\-(){}[\]<>:/\\|@#$%^&*+=`~]/
    // Pattern for spoken words, including accented characters
    const ALPHANUMERIC_RE = /^[a-zA-Z0-9\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF]+$/

    const DEFAULT_AVERAGE_CHARS_PER_TOKEN = 6
    // For languages similar to English, define a rough average
    // number of characters per token
    const LANGUAGE_METRICS = [
        { regex: /[äöüßÄÖÜẞ]/, averageCharsPerToken: 3 },
    ]

    // Split by whitespace, punctuation, and other special characters
    const roughTokens = input
        ?.split(/(\s+|[.,!?;'"„“”‘’\-(){}[\]<>:/\\|@#$%^&*+=`~]+)/)
        .filter(Boolean)

    let tokenCount = 0
    if (!roughTokens?.length) return tokenCount
    for (const token of roughTokens) {
        let averageCharsPerToken;
        for (const language of LANGUAGE_METRICS) {
            if (language.regex.test(token)) {
                averageCharsPerToken = language.averageCharsPerToken
                break
            }
        }

        if (WHITESPACE_RE.test(token)) {
            // Don't count whitespace as a token
            continue
        }
        else if (CJK_RE.test(token)) {
            // For CJK languages, each character is usually a separate token
            tokenCount += Array.from(token).length
        }
        else if (NUMERIC_SEQUENCE_RE.test(token)) {
            // Numeric sequences are often a single token, regardless of length
            tokenCount += 1
        }
        else if (token.length <= 3) {
            // Short tokens are often a single token
            tokenCount += 1
        }
        else if (PUNCTUATION_RE.test(token)) {
            // Punctuation is often a single token, but multiple punctuations are often split
            tokenCount += token.length > 1 ? Math.ceil(token.length / 2) : 1
        }
        else if (ALPHANUMERIC_RE.test(token) || averageCharsPerToken) {
            // Use language-specific average characters per token or default to average
            tokenCount += Math.ceil(token.length / (averageCharsPerToken ?? DEFAULT_AVERAGE_CHARS_PER_TOKEN))
        }
        else {
            // For other characters (like emojis or special characters), or languages
            // like Arabic, Hebrew and Greek, count each as a token
            tokenCount += Array.from(token).length
        }
    }

    return tokenCount
}

export function generateShareId(JSONstring) {
    return sha256(JSONstring).toString().substring(0,16);
}

export function calcTokensOpacity(totalTokens) {
    return 0.3 + totalTokens / 10000;
}

export function calcTokensColor(totalTokens) {
    return totalTokens > 50000 ? colorEasyRed : undefined
}

// these messages are handled by onRequest code module, so we want to filter them, no mather of the size
const unfilterableMessageTypes = [MESSAGE.TEXT_DETECTIONS];

export function countTotalTokens(messages) {
    let totalTokens = 0;
    messages.forEach(message => {
        Object.keys(message).forEach(key => {
            totalTokens += approximateTokenSize(message?.[key]?.toString());
        });
    });
    return totalTokens;
}

export function estimateItemTokens(item) {
    if (item?.item && item?.meta?.attributes) {
        const decryptedItem = decryptItemByAttributes(item.item, item.meta.attributes);
        const yamlString = toYAML(decryptedItem);
        return approximateTokenSize(yamlString);
    }
    return 0;
}

export function estimateTotalTokens(items) {
    return items.reduce((total, item) => total + estimateItemTokens(item), 0);
}

export function limitedChatMessages(chatMessages, params) {
    // ignore limitations on special messages
    if (chatMessages.find(o => o?.content && unfilterableMessageTypes.includes(parseJSON(o.content)?.type))) {
        return chatMessages;
    }

    // ignore limitations for publicChatData where KB data is not available
    if (publicChatData) {
        return chatMessages;
    }

    const lastXMessages = [];
    let imageEditorMessageIndex = -1;

    // Check if the first message is an IMAGE_EDITOR and add it to lastXMessages if it is
    if (parseJSON(chatMessages[0]?.content)?.type === 'IMAGE_EDITOR') {
        lastXMessages.push(chatMessages[0]);
        imageEditorMessageIndex = 0;
    }

    let frontendTokenLimit = params.frontendTokenLimit ||
        params.model && ChatModels?.[params.model]?.frontendTokenLimit;

    if (!frontendTokenLimit) frontendTokenLimit = 16000;

    for (let i = chatMessages.length - 1; i >= 0; i--) {
        // Skip the IMAGE_EDITOR message since it's already added
        if (i === imageEditorMessageIndex) {
            continue;
        }
        // Check if adding the current message would exceed the token limit
        if (countTotalTokens([...lastXMessages, chatMessages[i]]) > frontendTokenLimit) {
            break;
        }
        // Add the current message to the beginning of lastXMessages if it doesn't exceed the limit
        if (imageEditorMessageIndex === 0) {
            // If IMAGE_EDITOR message is at index 0, insert other messages after it
            lastXMessages.splice(1, 0, chatMessages[i]);
        } else {
            // If there's no IMAGE_EDITOR message at index 0, just unshift normally
            lastXMessages.unshift(chatMessages[i]);
        }
    }

    return lastXMessages;
}

// Helper function to format the date of the message
export const formatMessageDateChange = (timestamp) => {
    const date = new Date(timestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day < 10 ? '0' : ''}${day}`; // e.g., "Feb 25"
};

export const formatMessageTime = (timestamp, renderSettings) => {
    if (renderSettings?.formatMessageTime && typeof renderSettings.formatMessageTime === 'function') {
        return renderSettings.formatMessageTime(timestamp);
    }

    const date = new Date(timestamp);
    const now = new Date();

    const isToday = date.toDateString() === now.toDateString();

    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const strHours = hours < 10 ? `0${hours}` : hours.toString();
    const strMinutes = minutes < 10 ? `0${minutes}` : minutes.toString();
    const timeString = `${strHours}:${strMinutes} ${ampm}`; // e.g., "08:22 AM"

    if (!isToday) {
        const day = date.getDate();
        const month = date.toLocaleString('default', { month: 'short' }); // e.g., "Jan"
        return `${month} ${day} ${timeString}`; // e.g., "Jan 12 08:22 AM"
    }

    return timeString;
};

// Function to determine if we should show the date divider (remains unchanged)
export const shouldShowDateDivider = (currentTimestamp, previousTimestamp) => {
    if (!previousTimestamp) return true;
    const currentDate = new Date(currentTimestamp).toDateString();
    const previousDate = new Date(previousTimestamp).toDateString();

    return currentDate !== previousDate;
};

export function msgIdToTimestamp(msgId) {
    return parseInt(msgId?.split('-')[0]);
}

export function parseChatId() {
    return window?.location?.pathname?.match?.(/\/chat\/([\w-]+)/)?.[1]
}

export const loadChatHistory = (kbId, setMessages) => {
    const savedMessages = localStorage.getItem(`kbChat_${kbId}`);
    if (savedMessages) {
        setMessages(JSON.parse(savedMessages));
    }
};

export const clearChatHistory = (kbId, setMessages) => {
    localStorage.removeItem(`kbChat_${kbId}`);
    setMessages([]);
};

export const saveChatHistory = (kbId, messages) => {
    localStorage.setItem(`kbChat_${kbId}`, JSON.stringify(messages));
};

function base64Encode(value) {
    return Buffer.from(value).toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export let recentReactions = {};
let timeout = null;
export function trackReaction(msgId, emojiId) {
    const userId = kbUserDataLoaded?.myUserId;
    const reactionKey = base64Encode(`${userId}###${emojiId}`);
    if (!recentReactions[msgId]) recentReactions[msgId] = new Set();
    recentReactions?.[msgId].add(reactionKey);
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => recentReactions = {}, 60000); // cleanup
}

// Adjust the transformReactions function to filter out recently deleted reactions
export const transformReactions = (reactionsSet) => {
    if (!reactionsSet || !Array.isArray(reactionsSet)) return {};
    return reactionsSet ? reactionsSet.reduce((acc, reaction) => {
        const [encodedUserId, emojiId] = Buffer.from(reaction, 'base64').toString().split('###');
        if (!acc[emojiId]) {
            acc[emojiId] = {};
        }
        acc[emojiId][encodedUserId] = true; // Use true as a placeholder value
        return acc;
    }, {}) : {};
};

let blockedDuration = 5000;
let lastTimeBlocked = null;
export const blockAutoscroll = (data) => lastTimeBlocked = data || +new Date()
export const autoscrollBlocked = () => lastTimeBlocked > (+new Date() - blockedDuration)